import React, { useState, useEffect, useMemo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { AlertCircle, ArrowRight, Building2, Rocket, Users } from "lucide-react";
import { api } from "../lib/api";
import { useAuth, redirectByRole } from "../lib/auth";
import { setToken, setStoredUser } from "../lib/api";
import { toast } from "sonner";

/**
 * Phase 13.1 — Single-page complimentary signup.
 * No plan selector, no checkout. 7 fields + audience picker.
 */

const AUDIENCE_CARDS = [
  { key: "entrepreneur", label: "Entrepreneur / Founder",   Icon: Rocket,    blurb: "Build without a CTO." },
  { key: "business",     label: "Business / Team Leader",   Icon: Users,     blurb: "AI workflows for ops + leadership." },
  { key: "automotive",   label: "Automotive Dealer",        Icon: Building2, blurb: "AI playbooks for dealerships." },
];

const AUD_URL_MAP = {
  automotive: "automotive",
  business: "business",
  entrepreneur: "entrepreneur",
  entrepreneurs: "entrepreneur",
  general_business: "business",
};

// E.164 normalizer — US default. Light-touch validation.
function normalizeCellphone(raw) {
  if (!raw) return "";
  const digits = raw.replace(/[^\d+]/g, "");
  if (!digits) return "";
  if (digits.startsWith("+")) return digits;
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith("1")) return `+${digits}`;
  return `+${digits}`;
}
function cellphoneLooksValid(raw) {
  const d = (raw || "").replace(/\D/g, "");
  return d.length >= 10 && d.length <= 15;
}

export default function Register() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [params] = useSearchParams();
  const audienceFromUrl = AUD_URL_MAP[params.get("audience")] || null;

  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    cellphone: "",
    password: "",
    company_name: "",
    audience: audienceFromUrl || "",
  });
  const [autoFields, setAutoFields] = useState({}); // automotive-only follow-up answers
  const [autoQs, setAutoQs] = useState([]);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  // Phase 13.1 hotfix — touched / submit-attempted state so we don't fire
  // "Required / Valid email / 6+ characters" before the user interacts.
  const [touched, setTouched] = useState({});
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const markTouched = (field) => setTouched((t) => ({ ...t, [field]: true }));
  const showError = (field) => (touched[field] || submitAttempted) && !!validation[field];

  // Load automotive-only follow-up questions when audience flips to automotive.
  useEffect(() => {
    if (form.audience !== "automotive") {
      setAutoQs([]);
      return;
    }
    api.get("/registration-questions-by-audience?audience=automotive")
      .then((r) => setAutoQs(r.data.questions || []))
      .catch((e) => console.error("regq load failed", e));
  }, [form.audience]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validation = useMemo(() => {
    const errors = {};
    if (!form.first_name.trim()) errors.first_name = "Required";
    if (!form.last_name.trim()) errors.last_name = "Required";
    if (!/\S+@\S+\.\S+/.test(form.email)) errors.email = "Valid email required";
    if (!cellphoneLooksValid(form.cellphone)) errors.cellphone = "10+ digits required";
    if ((form.password || "").length < 6) errors.password = "6+ characters";
    if (!form.company_name.trim()) errors.company_name = "Required";
    if (!form.audience) errors.audience = "Pick one";
    return errors;
  }, [form]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitAttempted(true);
    if (Object.keys(validation).length > 0) {
      setError("Please complete the highlighted fields.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        email: form.email.trim().toLowerCase(),
        cellphone: normalizeCellphone(form.cellphone),
        password: form.password,
        company_name: form.company_name.trim(),
        audience: form.audience,
        custom_fields: form.audience === "automotive" ? autoFields : null,
      };
      const r = await api.post("/auth/register", payload);
      // Phase 18.3 — register response no longer includes token/user (byte-identical
      // shape prevents email enumeration). Auto-login via /auth/login for fresh
      // accounts. Duplicate emails naturally fail login with 401 and land the user
      // on the "sign in or reset password" prompt without confirming the email exists.
      const _regBody = r.data || {};
      let loginRes;
      try {
        loginRes = await api.post("/auth/login", {
          email: payload.email,
          password: payload.password,
        });
      } catch (loginErr) {
        const status = loginErr?.response?.status;
        if (status === 401) {
          // Password didn't match an existing account — probable duplicate email.
          setError("An account for this email may already exist. Sign in or reset your password.");
          return;
        }
        if (status === 429) {
          setError("Too many attempts right now. Please wait a moment and try again.");
          return;
        }
        throw loginErr;
      }
      const { token, user } = loginRes.data || {};
      if (!token) {
        throw new Error("No token in login response");
      }
      // 1) Persist token via the api helper so the request interceptor picks it
      //    up immediately (localStorage.setItem alone works because the
      //    interceptor reads the key on every call, but using the helper keeps
      //    this in sync with the rest of the auth surface).
      setToken(token);
      if (user) {
        setStoredUser(user);
        setUser(user);
      }
      // 2) Best-effort fetch a fresh /me to pull audience + assessment flags.
      let me = user;
      try {
        const r2 = await api.get("/auth/me");
        me = r2.data;
        setStoredUser(me);
        setUser(me);
      } catch (err) {
        // Non-fatal — we still have `user` from the register response.
        console.error("post-register /auth/me failed (continuing)", err);
      }
      // 3) Hard-navigate by role. `redirectByRole` is a PURE function that
      //    returns a path; it does NOT navigate. We call navigate() ourselves.
      const dest = redirectByRole(me?.role || "registered");
      navigate(dest, { replace: true });
    } catch (err) {
      console.error("register failed", err);
      const detail = err.response?.data?.detail;
      // If the register call itself succeeded but a downstream step threw,
      // surface a recoverable message instead of leaving the user stuck.
      if (err.message === "No token in register response") {
        toast.error("Account created but we couldn't sign you in. Please go to Login.");
        setError("Sign-in step failed. Click Sign in (top right) to access your account.");
      } else {
        setError(detail || "Sign-up failed. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-fc-navy text-white flex flex-col lg:flex-row" data-testid="register-page">
      {/* Hero panel (right side on desktop, top on mobile) */}
      <aside className="lg:w-2/5 bg-[#060A24] flex items-center px-8 lg:px-14 py-12 order-1 lg:order-2 border-b lg:border-b-0 lg:border-l border-white/5">
        <div>
          <p className="fc-overline mb-4 text-amber-300">FREDDY COMPUTE ACADEMY</p>
          <h2 className="text-3xl lg:text-4xl font-black tracking-tight mb-5 leading-tight">
            Complimentary access<br/>during early access.
          </h2>
          <p className="text-white/65 text-base leading-relaxed mb-8 max-w-md">
            Live AI training events, exclusive community, and courses launching soon.
            Reserve your seat at the next live event the moment you join.
          </p>
          <ul className="space-y-3 text-sm text-white/55">
            <li className="flex items-start gap-2"><span className="text-amber-300">·</span> Weekly live sessions with operators</li>
            <li className="flex items-start gap-2"><span className="text-amber-300">·</span> Track tailored to your work</li>
            <li className="flex items-start gap-2"><span className="text-amber-300">·</span> Community access at launch</li>
          </ul>
          <p className="text-[11px] text-white/40 mt-10">
            Already have an account? <Link to="/login" className="text-amber-300 font-bold hover:underline">Sign in</Link>
          </p>
        </div>
      </aside>

      {/* Form panel */}
      <main className="flex-1 px-6 sm:px-10 lg:px-16 py-12 order-2 lg:order-1" data-testid="register-form-panel">
        <div className="max-w-xl mx-auto">
          <p className="text-[11px] tracking-[0.22em] uppercase font-bold text-amber-300 mb-3">GET COMPLIMENTARY ACCESS</p>
          <h1 className="text-3xl sm:text-4xl font-black tracking-tight mb-3">
            Reserve your seat.
          </h1>
          <p className="text-white/55 text-sm mb-8">
            One form. Land on your dashboard the moment you submit.
          </p>

          {error && (
            <div className="bg-fc-red/10 border border-fc-red/40 text-fc-red px-4 py-3 mb-6 text-sm flex items-start gap-2" data-testid="register-error">
              <AlertCircle size={16} className="mt-0.5 flex-shrink-0" /> {error}
            </div>
          )}

          <form onSubmit={onSubmit} className="space-y-5">
            {/* First / Last name — side by side on sm+ */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="First Name" required error={showError("first_name") ? validation.first_name : null} testid="register-first-name-field">
                <input
                  type="text" value={form.first_name}
                  onChange={(e) => set("first_name", e.target.value)}
                  onBlur={() => markTouched("first_name")}
                  required autoFocus autoComplete="given-name"
                  className={inputCls(showError("first_name"))}
                  data-testid="register-first-name"
                />
              </Field>
              <Field label="Last Name" required error={showError("last_name") ? validation.last_name : null} testid="register-last-name-field">
                <input
                  type="text" value={form.last_name}
                  onChange={(e) => set("last_name", e.target.value)}
                  onBlur={() => markTouched("last_name")}
                  required autoComplete="family-name"
                  className={inputCls(showError("last_name"))}
                  data-testid="register-last-name"
                />
              </Field>
            </div>

            <Field label="Email" required error={showError("email") ? validation.email : null}>
              <input
                type="email" value={form.email}
                onChange={(e) => set("email", e.target.value)}
                onBlur={() => markTouched("email")}
                required autoComplete="email"
                className={inputCls(showError("email"))}
                data-testid="register-email"
              />
            </Field>

            <Field label="Cell Phone" required error={showError("cellphone") ? validation.cellphone : null} hint="We'll text you 1h before each live event.">
              <input
                type="tel" value={form.cellphone}
                onChange={(e) => set("cellphone", e.target.value)}
                onBlur={() => markTouched("cellphone")}
                required autoComplete="tel"
                placeholder="+1 (555) 123-4567"
                className={inputCls(showError("cellphone"))}
                data-testid="register-cellphone"
              />
            </Field>

            <Field label="Password" required error={showError("password") ? validation.password : null} hint="Minimum 6 characters.">
              <input
                type="password" value={form.password}
                onChange={(e) => set("password", e.target.value)}
                onBlur={() => markTouched("password")}
                required minLength={6} autoComplete="new-password"
                className={inputCls(showError("password"))}
                data-testid="register-password"
              />
            </Field>

            <Field label="Company Name" required error={showError("company_name") ? validation.company_name : null}>
              <input
                type="text" value={form.company_name}
                onChange={(e) => set("company_name", e.target.value)}
                onBlur={() => markTouched("company_name")}
                required autoComplete="organization"
                className={inputCls(showError("company_name"))}
                data-testid="register-company-name"
              />
            </Field>

            {/* Audience picker — 3 cards */}
            <div>
              <span className={labelCls(showError("audience"))}>Your Track</span>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-2" data-testid="register-audience-picker">
                {AUDIENCE_CARDS.map((a) => {
                  const Icon = a.Icon;
                  const on = form.audience === a.key;
                  return (
                    <button
                      key={a.key} type="button"
                      onClick={() => { set("audience", a.key); markTouched("audience"); }}
                      className={`p-4 text-left border transition-all ${on ? "border-amber-400 bg-amber-500/10" : "border-white/15 hover:border-white/40 bg-[#060A24]"}`}
                      data-testid={`register-audience-${a.key}`}
                    >
                      <Icon size={18} className={on ? "text-amber-300 mb-2" : "text-white/55 mb-2"} />
                      <p className="text-xs font-black tracking-wide uppercase">{a.label}</p>
                      <p className="text-[11px] text-white/55 mt-1 leading-relaxed">{a.blurb}</p>
                    </button>
                  );
                })}
              </div>
              {showError("audience") && <p className="text-red-400 text-xs mt-2">{validation.audience}</p>}
            </div>

            {/* Automotive-only inline follow-up questions */}
            {form.audience === "automotive" && autoQs.length > 0 && (
              <div className="border-t border-white/5 pt-5 space-y-4" data-testid="register-automotive-section">
                <p className="text-[10px] tracking-[0.2em] uppercase font-bold text-white/55">A few quick details (Automotive)</p>
                {autoQs.map((q) => (
                  <AutoQuestion key={q.id} q={q}
                                value={autoFields[q.id]}
                                onChange={(v) => setAutoFields((s) => ({ ...s, [q.id]: v }))} />
                ))}
              </div>
            )}

            <button
              type="submit" disabled={submitting}
              className="w-full px-6 py-3.5 text-xs font-black tracking-[0.22em] uppercase bg-amber-500 hover:bg-amber-400 text-fc-navy transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              data-testid="register-submit"
            >
              {submitting ? "Reserving your seat…" : <>Get Complimentary Access <ArrowRight size={14} /></>}
            </button>
            <p className="text-[10px] text-white/35 text-center">
              By joining you agree to our terms. Complimentary during early access — no charge at this time.
            </p>
          </form>
        </div>
      </main>
    </div>
  );
}

function inputCls(hasError) {
  return `w-full bg-[#060A24] border ${hasError ? "border-red-500" : "border-white/20 focus:border-amber-400"} text-white px-4 py-2.5 text-sm outline-none transition-colors`;
}

function labelCls(hasError) {
  return `text-[10px] tracking-[0.2em] uppercase font-bold block mb-2 ${hasError ? "text-red-400" : "text-white/90"}`;
}

function Field({ label, error, hint, children, testid, required }) {
  return (
    <label className="block" data-testid={testid}>
      <span className={labelCls(!!error)}>
        {label}
        {required && <span className="text-red-500 ml-0.5" aria-label="required">*</span>}
      </span>
      {children}
      {error && <p className="text-red-400 text-[11px] mt-1.5">{error}</p>}
      {!error && hint && <p className="text-white/40 text-[11px] mt-1.5">{hint}</p>}
    </label>
  );
}

function AutoQuestion({ q, value, onChange }) {
  if (q.type === "select" || q.type === "single_choice") {
    return (
      <div>
        <p className="text-xs text-white/65 mb-2">{q.label}{q.required && <span className="text-fc-red ml-1">*</span>}</p>
        <select value={value || ""} onChange={(e) => onChange(e.target.value)}
                className="w-full bg-[#060A24] border border-white/15 px-3 py-2 text-sm">
          <option value="">Select…</option>
          {(q.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      </div>
    );
  }
  return (
    <div>
      <p className="text-xs text-white/65 mb-2">{q.label}{q.required && <span className="text-fc-red ml-1">*</span>}</p>
      <input type="text" value={value || ""} onChange={(e) => onChange(e.target.value)}
             className="w-full bg-[#060A24] border border-white/15 px-3 py-2 text-sm" />
    </div>
  );
}
